import { Component, ElementRef, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthSessionService } from '../../core/auth-session.service';

@Component({
  selector: 'app-user-menu',
  imports: [RouterLink],
  template: `
    <div class="user-menu">
      <button
        type="button"
        class="user-menu__trigger"
        aria-haspopup="menu"
        [attr.aria-expanded]="open()"
        (click)="toggle()"
      >
        <svg class="user-menu__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            fill="currentColor"
            d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5Z"
          />
        </svg>
        <span class="user-menu__identity">
          <span class="user-menu__name">{{ displayName() }}</span>
          @if (organization()) {
            <span class="user-menu__divider" aria-hidden="true">|</span>
            <span class="user-menu__org">{{ organization() }}</span>
          }
        </span>
        <svg class="user-menu__caret" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="m7 10 5 5 5-5Z" />
        </svg>
      </button>

      @if (open()) {
        <div class="user-menu__panel" role="menu">
          <a class="user-menu__item" role="menuitem" routerLink="/omat-tiedot" (click)="close()">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5Z"
              />
            </svg>
            Omat tiedot
          </a>
          <button class="user-menu__item" role="menuitem" type="button" (click)="logout()">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M16 13v-2H7V8l-5 4 5 4v-3h9Zm3-10H10a2 2 0 0 0-2 2v3h2V5h9v14h-9v-3H8v3a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Z"
              />
            </svg>
            Kirjaudu ulos
          </button>
        </div>
      }
    </div>
  `,
  styleUrl: './user-menu.component.scss',
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'close()',
  },
})
export class UserMenuComponent {
  private readonly session = inject(AuthSessionService);
  private readonly router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);

  protected readonly open = signal(false);
  protected readonly displayName = computed(() => {
    const identity = this.session.identity();
    if (!identity) return '';
    return identity.name || identity.email;
  });
  protected readonly organization = computed(() => this.session.identity()?.organization ?? '');

  protected toggle(): void {
    this.open.update((value) => !value);
  }

  protected close(): void {
    this.open.set(false);
  }

  protected onDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  protected async logout(): Promise<void> {
    this.close();
    await this.session.logout();
    await this.router.navigateByUrl('/');
  }
}
